import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Plus, Sparkles, BookOpen, Headphones, GraduationCap, Youtube, Zap, X,
  Check, ExternalLink, RefreshCw, Trash2, ChevronLeft, Loader2, GripVertical, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import {
  DndContext, closestCenter, useSensor, useSensors,
  PointerSensor, TouchSensor, KeyboardSensor, useDroppable,
} from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { nexaApi, photoUrl } from './api';

const STAGES = [
  { id: 'in_progress', label: 'In progress', hint: 'Actively working on this now', accent: 'sky' },
  { id: 'soon', label: 'Soon', hint: 'Queued up — starting next', accent: 'sky' },
  { id: 'future', label: 'Future', hint: 'Someday, aspirational', accent: 'sky' },
  { id: 'done', label: 'Accomplished', hint: 'You did it — this is done', accent: 'emerald' },
];
const STAGE_IDS = STAGES.map((s) => s.id);
const DEFAULT_STAGE = 'soon';

const KIND_META = {
  book: { label: 'Books', icon: BookOpen, color: 'text-amber-300' },
  audiobook: { label: 'Audiobooks', icon: Headphones, color: 'text-fuchsia-300' },
  course: { label: 'Courses', icon: GraduationCap, color: 'text-emerald-300' },
  youtube: { label: 'YouTube', icon: Youtube, color: 'text-rose-300' },
  mission: { label: 'Daily missions', icon: Zap, color: 'text-sky-300' },
};
const KIND_ORDER = ['mission', 'book', 'audiobook', 'course', 'youtube'];

const kindIcon = (kind) => {
  const M = KIND_META[kind] || KIND_META.mission;
  return M;
};

// ---------------- Add skill modal ----------------
const AddSkillModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [why, setWhy] = useState('');
  const [stage, setStage] = useState(DEFAULT_STAGE);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await nexaApi.post('/skills', {
        name: name.trim(),
        why: why.trim() || null,
        stage,
      });
      toast.success(`Added — ${data.name}`);
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add the skill');
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
      data-testid="nexa-add-skill-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-light nexa-heading">Add a skill</h3>
          <button onClick={onClose} disabled={busy} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/60">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="text-[11px] font-mono tracking-[0.2em] text-white/40 uppercase">Skill / goal</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Talk with a deep voice"
          data-testid="nexa-add-skill-name"
          className="nexa-input w-full rounded-xl px-4 py-3 text-sm mt-2 mb-4"
          autoFocus
        />
        <label className="text-[11px] font-mono tracking-[0.2em] text-white/40 uppercase">When?</label>
        <div className="grid grid-cols-3 gap-2 mt-2 mb-4" data-testid="nexa-add-skill-stage">
          {STAGES.filter((s) => s.id !== 'done').map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStage(s.id)}
              data-testid={`nexa-add-skill-stage-${s.id}`}
              className={`rounded-xl px-2 py-2.5 text-xs font-medium transition-colors border ${
                stage === s.id
                  ? 'bg-sky-400 text-black border-sky-400'
                  : 'bg-white/[0.02] text-white/70 border-white/10 hover:border-white/25'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-white/40 mb-4 -mt-2">
          {STAGES.find((s) => s.id === stage)?.hint}
        </p>

        <label className="text-[11px] font-mono tracking-[0.2em] text-white/40 uppercase">Why does it matter?</label>
        <textarea
          rows={2}
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="Optional — helps NEXA personalise the plan"
          data-testid="nexa-add-skill-why"
          className="nexa-input w-full rounded-xl px-4 py-3 text-sm resize-none mt-2 mb-4"
        />

        <p className="text-[11px] text-white/35 mb-4">
          NEXA will study your personality and suggest books, courses, YouTube lessons and daily missions.
        </p>

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={submit}
          disabled={!name.trim() || busy}
          data-testid="nexa-add-skill-submit"
          className="w-full h-12 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              NEXA is drafting your plan…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Create & get a plan
            </>
          )}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// ---------------- Suggestion preview modal (option C) ----------------
const SuggestionPreview = ({ suggestion, onAdd, onSkip, onClose, busy }) => {
  if (!suggestion) return null;
  const K = kindIcon(suggestion.kind);
  const Icon = K.icon;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[65] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="nexa-suggestion-preview"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0A0A0A] p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center ${K.color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono tracking-[0.25em] text-white/40 uppercase">{K.label}</div>
            <div data-testid="nexa-suggestion-preview-title"
                 className="text-white text-base font-medium leading-snug break-words">{suggestion.title}</div>
            {suggestion.author && (
              <div className="text-white/50 text-xs mt-0.5 break-words">{suggestion.author}</div>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/60">
            <X className="w-4 h-4" />
          </button>
        </div>

        {suggestion.why && (
          <div className="rounded-2xl border border-sky-400/20 bg-sky-400/5 p-4 mb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="w-3.5 h-3.5 text-sky-400" />
              <span className="text-[10px] font-mono tracking-[0.25em] text-sky-300 uppercase">Why NEXA picked this</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{suggestion.why}</p>
          </div>
        )}

        {suggestion.url && (
          <a
            href={suggestion.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-sm text-sky-300 hover:text-sky-200 mb-5 truncate"
            data-testid="nexa-suggestion-url"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            <span className="truncate">{suggestion.url}</span>
          </a>
        )}

        <div className="flex gap-3">
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={onSkip}
            disabled={busy}
            data-testid="nexa-suggestion-skip"
            className="flex-1 h-12 rounded-xl border border-white/15 text-white/80 text-sm disabled:opacity-40"
          >
            Skip
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={onAdd}
            disabled={busy}
            data-testid="nexa-suggestion-add"
            className="flex-[2] h-12 rounded-xl bg-white text-black font-semibold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add to my plan
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ---------------- Add-your-own inline form ----------------
const AddOwnItem = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('mission');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setKind('mission'); setTitle(''); setAuthor(''); setUrl('');
  };

  const submit = async () => {
    if (!title.trim()) { toast.error('Give it a title first'); return; }
    setBusy(true);
    const ok = await onAdd({
      kind, title: title.trim(),
      author: author.trim() || undefined,
      url: url.trim() || undefined,
    });
    setBusy(false);
    if (ok) { reset(); setOpen(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="nexa-skill-add-own"
        className="w-full h-12 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] text-white/60 flex items-center justify-center gap-2 text-xs tracking-[0.18em] uppercase font-mono hover:border-sky-400/40 hover:text-sky-300 transition-colors"
      >
        <Plus className="w-4 h-4" strokeWidth={2} /> Add my own task
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="nexa-skill-add-own-form"
      className="rounded-2xl border border-sky-400/30 bg-sky-400/[0.04] p-4 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.2em] uppercase font-mono text-sky-300">Add your own</span>
        <button onClick={() => { reset(); setOpen(false); }} data-testid="nexa-skill-add-own-close"
          className="text-white/40 hover:text-white/70">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {KIND_ORDER.map((k) => {
          const M = KIND_META[k]; const I = M.icon; const active = kind === k;
          return (
            <button key={k} onClick={() => setKind(k)}
              data-testid={`nexa-skill-kind-${k}`}
              className={`flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] transition-colors ${
                active
                  ? 'border border-sky-400/60 bg-sky-400/10 text-sky-100'
                  : 'border border-white/10 bg-white/[0.02] text-white/60'
              }`}
            >
              <I className={`w-3 h-3 ${active ? 'text-sky-300' : M.color}`} strokeWidth={1.8} />
              {M.label.replace(/s$/, '')}
            </button>
          );
        })}
      </div>
      <input
        value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder={kind === 'mission' ? 'e.g. Read 30 min in bed' : 'Title'}
        data-testid="nexa-skill-add-own-title"
        className="bg-transparent text-white text-sm border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder-white/30"
      />
      {kind !== 'mission' && (
        <>
          <input
            value={author} onChange={(e) => setAuthor(e.target.value)}
            placeholder="Author / creator / channel (optional)"
            data-testid="nexa-skill-add-own-author"
            className="bg-transparent text-white text-sm border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder-white/30"
          />
          <input
            value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste a link (optional — I'll build a search link if you leave this blank)"
            data-testid="nexa-skill-add-own-url"
            className="bg-transparent text-white text-sm border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder-white/30"
          />
        </>
      )}
      <button onClick={submit} disabled={busy}
        data-testid="nexa-skill-add-own-submit"
        className="h-10 rounded-xl bg-sky-400 text-black text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[.98] transition-transform"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
        Add to my plan
      </button>
    </motion.div>
  );
};

// ---------------- Item row ----------------
const ItemRow = ({ item, onToggle, onDelete }) => {
  const K = kindIcon(item.kind);
  const Icon = K.icon;
  const isMission = item.kind === 'mission';
  const done = isMission ? !!item.done_today : item.status === 'done';
  const streak = isMission ? (item.streak || 0) : 0;
  return (
    <div
      data-testid={`nexa-skill-item-${item.id}`}
      className={`group flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-3 ${done && !isMission ? 'opacity-60' : ''}`}
    >
      <button
        onClick={() => onToggle(item)}
        aria-label={done ? 'Mark not done' : 'Mark done'}
        data-testid={`nexa-skill-item-toggle-${item.id}`}
        className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
          done ? 'bg-sky-400 border-sky-400 text-black' : 'border-white/25 hover:border-sky-400/70'
        }`}
      >
        {done && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
      </button>
      <Icon className={`w-4 h-4 shrink-0 mt-1.5 ${K.color}`} strokeWidth={1.7} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm text-white leading-snug break-words ${done && !isMission ? 'line-through' : ''}`}>
          {item.title}
        </div>
        {isMission ? (
          <div className="text-[11px] text-white/45 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
            {streak > 0 && (
              <span className="text-amber-300" data-testid={`nexa-mission-streak-${item.id}`}>
                🔥 {streak}-day streak
              </span>
            )}
            <span>{item.last7_done || 0}/7 last week</span>
            {item.why && !streak && <span className="break-words">· {item.why}</span>}
          </div>
        ) : (
          (item.author || item.why) && (
            <div className="text-[11px] text-white/45 break-words mt-0.5">
              {item.author || item.why}
            </div>
          )
        )}
      </div>
      {item.url && (
        <a href={item.url} target="_blank" rel="noreferrer"
          className="text-white/40 hover:text-sky-300 p-1 shrink-0 mt-0.5"
          data-testid={`nexa-skill-item-url-${item.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
      <button
        onClick={() => onDelete(item)}
        aria-label="Delete"
        data-testid={`nexa-skill-item-delete-${item.id}`}
        className="text-white/30 hover:text-rose-400 p-1 shrink-0 mt-0.5"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// ---------------- Skill detail ----------------
const SkillDetail = ({ skillSummary, onBack, onChanged }) => {
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await nexaApi.get(`/skills/${skillSummary.id}`);
      setSkill(data);
    } catch (e) {
      toast.error('Could not load skill');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [skillSummary.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const g = {};
    (skill?.items || []).forEach((it) => {
      (g[it.kind] = g[it.kind] || []).push(it);
    });
    return g;
  }, [skill]);

  const acceptSuggestion = async () => {
    if (!preview) return;
    setPreviewBusy(true);
    try {
      const { data } = await nexaApi.post(
        `/skills/${skill.id}/suggestions/${preview.id}/accept`);
      setSkill((s) => ({
        ...s,
        items: [...(s.items || []), data],
        suggestions: (s.suggestions || []).filter((x) => x.id !== preview.id),
      }));
      toast.success(`Added — ${data.title}`);
      setPreview(null);
      onChanged();
    } catch {
      toast.error('Could not add');
    } finally {
      setPreviewBusy(false);
    }
  };

  const skipSuggestion = async () => {
    if (!preview) return;
    setPreviewBusy(true);
    try {
      await nexaApi.delete(`/skills/${skill.id}/suggestions/${preview.id}`);
      setSkill((s) => ({
        ...s,
        suggestions: (s.suggestions || []).filter((x) => x.id !== preview.id),
      }));
      setPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const toggleItem = async (item) => {
    const currentDone = item.kind === 'mission' ? !!item.done_today : item.status === 'done';
    const nextStatus = currentDone ? 'pending' : 'done';
    try {
      const { data } = await nexaApi.patch(
        `/skills/${skill.id}/items/${item.id}`, { status: nextStatus });
      setSkill((s) => ({
        ...s,
        items: s.items.map((x) => (x.id === item.id ? data : x)),
      }));
      onChanged();
    } catch {
      toast.error('Could not update item');
    }
  };

  const deleteItem = async (item) => {
    try {
      await nexaApi.delete(`/skills/${skill.id}/items/${item.id}`);
      setSkill((s) => ({ ...s, items: s.items.filter((x) => x.id !== item.id) }));
      onChanged();
    } catch {
      toast.error('Could not delete');
    }
  };

  const addOwnItem = async (payload) => {
    try {
      const { data } = await nexaApi.post(`/skills/${skill.id}/items`, payload);
      setSkill((s) => ({ ...s, items: [...(s.items || []), data] }));
      onChanged();
      toast.success('Added to your plan');
      return true;
    } catch {
      toast.error('Could not add task');
      return false;
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await nexaApi.post(`/skills/${skill.id}/suggest`);
      setSkill((s) => ({ ...s, suggestions: data.suggestions || [] }));
      toast.success(`NEXA added ${data.suggestions?.length || 0} fresh ideas`);
    } catch {
      toast.error('Could not fetch new suggestions');
    } finally {
      setRegenerating(false);
    }
  };

  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const regenerateImage = async () => {
    if (regeneratingImage) return;
    setRegeneratingImage(true);
    // Show the loading state immediately by clearing the current photo id.
    setSkill((s) => ({ ...s, illustration_photo_id: null }));
    try {
      const { data } = await nexaApi.post(`/skills/${skill.id}/illustration`);
      setSkill((s) => ({ ...s, illustration_photo_id: data.illustration_photo_id }));
      onChanged();
      toast.success('New mascot ready');
    } catch {
      toast.error('Could not regenerate the mascot');
    } finally {
      setRegeneratingImage(false);
    }
  };

  if (loading || !skill) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-sky-400" />
      </div>
    );
  }

  const suggestions = skill.suggestions || [];

  return (
    <div className="flex flex-col gap-5" data-testid={`nexa-skill-detail-${skill.id}`}>
      <button
        onClick={onBack}
        data-testid="nexa-skill-back"
        className="self-start flex items-center gap-1.5 text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 hover:text-sky-400"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Personal Growth
      </button>

      <div>
        <div className="text-[10px] font-mono tracking-[0.3em] text-sky-400/80 uppercase mb-1.5">Skill</div>
        <h2 className="nexa-heading text-3xl font-light tracking-tight">{skill.name}</h2>
        {skill.why && <p className="text-white/50 text-sm mt-2 leading-relaxed">{skill.why}</p>}
      </div>

      {/* Mascot illustration + regenerate control */}
      <div className="relative rounded-2xl border border-white/10 bg-black overflow-hidden"
           data-testid="nexa-skill-detail-illustration">
        <div className="w-full aspect-[16/9] flex items-center justify-center">
          {skill.illustration_photo_id ? (
            <img
              src={photoUrl(skill.illustration_photo_id)}
              alt=""
              className="w-full h-full object-contain object-center"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-sky-400/50">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-[9px] font-mono tracking-[0.25em] uppercase">
                {regeneratingImage ? 'Redrawing…' : 'Drawing…'}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={regenerateImage}
          disabled={regeneratingImage}
          data-testid="nexa-skill-regenerate-image"
          className="absolute top-2 right-2 flex items-center gap-1.5 px-3 h-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/15 text-[11px] font-mono tracking-[0.15em] uppercase text-white/80 hover:text-sky-300 hover:border-sky-400/50 active:scale-95 transition-all disabled:opacity-40"
        >
          {regeneratingImage ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Regenerate
        </button>
      </div>

      {/* NEXA suggestions */}
      <div className="rounded-3xl border border-sky-400/20 bg-sky-400/[0.03] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-400" />
            <span className="text-xs tracking-[0.2em] uppercase text-white/60">NEXA suggests</span>
          </div>
          <button
            onClick={regenerate}
            disabled={regenerating}
            data-testid="nexa-skill-regenerate"
            className="text-[11px] text-white/60 hover:text-sky-300 flex items-center gap-1.5 disabled:opacity-40"
          >
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {suggestions.length ? 'More ideas' : 'Draft a plan'}
          </button>
        </div>
        {suggestions.length === 0 ? (
          <p className="text-sm text-white/45">
            {regenerating
              ? 'NEXA is thinking about the best resources for you…'
              : 'Tap "Draft a plan" and NEXA will suggest books, courses and daily missions.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {suggestions.map((s) => {
              const K = kindIcon(s.kind);
              const Icon = K.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setPreview(s)}
                  data-testid={`nexa-suggestion-${s.id}`}
                  className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/8 hover:border-sky-400/40 text-left transition-colors"
                >
                  <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${K.color}`} strokeWidth={1.7} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white leading-snug break-words">{s.title}</div>
                    <div className="text-[11px] text-white/45 break-words mt-0.5">
                      {s.author ? `${s.author} · ` : ''}{K.label}
                    </div>
                  </div>
                  <ChevronLeft className="w-4 h-4 text-white/30 rotate-180 shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Progress timeline */}
      <SkillTimeline items={skill.items} />

      {/* Items grouped by kind */}
      {KIND_ORDER.filter((k) => (grouped[k] || []).length > 0).map((k) => {
        const K = KIND_META[k];
        const Icon = K.icon;
        const items = grouped[k];
        const doneCount = k === 'mission'
          ? items.filter((i) => i.done_today).length
          : items.filter((i) => i.status === 'done').length;
        const suffix = k === 'mission' ? ' today' : '';
        return (
          <div key={k}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-4 h-4 ${K.color}`} strokeWidth={1.7} />
              <span className="text-[10px] font-mono tracking-[0.25em] text-white/50 uppercase">
                {K.label} · {doneCount}/{items.length}{suffix}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((it) => (
                <ItemRow key={it.id} item={it} onToggle={toggleItem} onDelete={deleteItem} />
              ))}
            </div>
          </div>
        );
      })}

      {(!skill.items || skill.items.length === 0) && (
        <p className="text-sm text-white/40 text-center py-4">
          Nothing added yet — tap a suggestion above or add your own below.
        </p>
      )}

      <AddOwnItem onAdd={addOwnItem} />

      <AnimatePresence>
        {preview && (
          <SuggestionPreview
            suggestion={preview}
            busy={previewBusy}
            onAdd={acceptSuggestion}
            onSkip={skipSuggestion}
            onClose={() => setPreview(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------- Skill progress timeline ----------------
const fmtDateLabel = (iso) => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayIso = `${yyyy}-${mm}-${dd}`;
  const y = new Date(today.getTime() - 86400000);
  const yesterdayIso = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
  if (iso === todayIso) return 'Today';
  if (iso === yesterdayIso) return 'Yesterday';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
};

const buildTimeline = (items) => {
  const events = [];
  (items || []).forEach((it) => {
    if (it.kind === 'mission') {
      (it.done_dates || []).forEach((d) => {
        events.push({ date: d, id: `${it.id}:${d}`, kind: 'mission', title: it.title });
      });
    } else if (it.status === 'done' && it.completed_at) {
      events.push({ date: it.completed_at.slice(0, 10), id: it.id, kind: it.kind, title: it.title });
    }
  });
  events.sort((a, b) => (a.date < b.date ? 1 : -1));
  const grouped = [];
  let last = null;
  for (const ev of events.slice(0, 30)) {
    if (!last || last.date !== ev.date) {
      last = { date: ev.date, items: [] };
      grouped.push(last);
    }
    last.items.push(ev);
  }
  return grouped;
};

const SkillTimeline = ({ items }) => {
  const groups = useMemo(() => buildTimeline(items), [items]);
  if (groups.length === 0) return null;
  const totalEvents = groups.reduce((a, g) => a + g.items.length, 0);
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-5" data-testid="nexa-skill-timeline">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
        <span className="text-xs tracking-[0.2em] uppercase text-white/60">Progress</span>
        <span className="text-[10px] text-white/35 ml-auto">
          {totalEvents} completion{totalEvents === 1 ? '' : 's'} · last {groups.length} day{groups.length === 1 ? '' : 's'}
        </span>
      </div>
      <ol className="relative border-l border-white/10 ml-1.5 pl-5 space-y-4">
        {groups.map((g) => (
          <li key={g.date} className="relative">
            <span className="absolute -left-[26px] top-1 w-2.5 h-2.5 rounded-full bg-sky-400 ring-4 ring-[#0A0A0A]" />
            <div className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/60 mb-1.5">
              {fmtDateLabel(g.date)}
            </div>
            <div className="flex flex-col gap-1">
              {g.items.map((ev) => {
                const K = kindIcon(ev.kind);
                const Icon = K.icon;
                return (
                  <div
                    key={ev.id}
                    className="flex items-center gap-2 text-sm text-white/80"
                    data-testid={`nexa-timeline-${ev.id}`}
                  >
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${K.color}`} strokeWidth={1.7} />
                    <span className="truncate">{ev.title}</span>
                    {ev.kind === 'mission' && (
                      <span className="text-[10px] text-white/40 shrink-0">daily</span>
                    )}
                  </div>
                );
              })}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

// ---------------- Sortable skill card (drag between stage groups) ----------------
const SkillCard = ({ skill, onOpen, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: skill.id,
    data: { stage: skill.stage || DEFAULT_STAGE },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const total = skill.item_count || 0;
  const done = skill.done_count || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isDone = skill.stage === 'done';
  const illustration = skill.illustration_photo_id
    ? photoUrl(skill.illustration_photo_id) : null;

  const bracket = isDone ? 'border-emerald-400/60' : 'border-sky-400/50';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`nexa-skill-${skill.id}`}
      className={`relative aspect-square rounded-2xl border overflow-hidden bg-[#0A0A0A] transition-colors ${
        isDone
          ? 'border-emerald-500/30 hover:border-emerald-400/60'
          : 'border-white/10 hover:border-sky-400/50 hover:shadow-[0_0_20px_rgba(56,189,248,0.15)]'
      } ${isDragging ? 'shadow-[0_0_30px_rgba(56,189,248,0.35)] border-sky-400/70' : ''}`}
    >
      {/* Fallout-style corner brackets — matches Life-page tiles */}
      <span className={`absolute top-1.5 left-1.5 w-2.5 h-2.5 border-t border-l ${bracket} pointer-events-none z-10`} />
      <span className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 border-t border-r ${bracket} pointer-events-none z-10`} />
      <span className={`absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-b border-l ${bracket} pointer-events-none z-10`} />
      <span className={`absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-b border-r ${bracket} pointer-events-none z-10`} />

      {/* Illustration as full-tile background */}
      <button
        onClick={() => onOpen(skill)}
        data-testid={`nexa-skill-open-${skill.id}`}
        className="absolute inset-0 w-full h-full flex flex-col justify-end p-3 text-left"
        aria-label={`Open ${skill.name}`}
      >
        {illustration ? (
          <img
            src={illustration}
            alt=""
            data-testid={`nexa-skill-illustration-${skill.id}`}
            className="absolute inset-0 w-full h-full object-cover object-center opacity-80"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-sky-400/40">
            <div className="w-6 h-6 rounded-full border-2 border-sky-400/25 border-t-sky-400 animate-spin" />
            <span className="text-[8px] font-mono tracking-[0.25em] uppercase">Drawing…</span>
          </div>
        )}

        {/* Bottom gradient for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black via-black/85 to-transparent pointer-events-none" />

        <div className="relative">
          <div className={`text-[11px] tracking-[0.18em] font-mono uppercase leading-tight line-clamp-2 ${
            isDone ? 'text-emerald-200' : 'text-white/90'
          }`}>
            {skill.name}
          </div>
          <div className="text-[9px] font-mono tracking-[0.2em] uppercase text-white/50 mt-1">
            {isDone ? 'Accomplished' : total > 0 ? `${done}/${total} · ${pct}%` : 'No items yet'}
          </div>
        </div>
      </button>

      {isDone && (
        <span className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[9px] font-mono tracking-[0.2em] uppercase text-emerald-300 bg-emerald-950/70 backdrop-blur-sm px-2 py-1 rounded-full ring-1 ring-emerald-400/40 z-20 pointer-events-none">
          <Check className="w-2.5 h-2.5" strokeWidth={3} /> Done
        </span>
      )}

      {/* Progress bar under everything */}
      {total > 0 && !isDone && (
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/70 overflow-hidden z-10">
          <div className="h-full bg-sky-400" style={{ width: `${pct}%` }} />
        </div>
      )}

      {/* Drag handle — top-right */}
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag skill"
        data-testid={`nexa-skill-drag-${skill.id}`}
        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white active:bg-black/80 touch-none cursor-grab active:cursor-grabbing z-20"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>

      {/* Delete — top-left */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(skill); }}
        aria-label="Delete skill"
        data-testid={`nexa-skill-delete-${skill.id}`}
        className="absolute top-1 left-1 w-7 h-7 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white/50 hover:text-rose-400 z-20"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ---------------- Droppable stage column ----------------
const StageColumn = ({ stage, skills, onOpen, onDelete }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `stage:${stage.id}`, data: { stage: stage.id } });
  const isDone = stage.id === 'done';
  const headerColor = isDone ? 'text-emerald-400/80' : 'text-sky-400/70';
  const overRing = isDone ? 'bg-emerald-500/[0.06] ring-1 ring-emerald-400/40' : 'bg-sky-400/[0.06] ring-1 ring-sky-400/40';
  return (
    <section data-testid={`nexa-stage-${stage.id}`}>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h3 className={`text-[11px] tracking-[0.25em] uppercase ${headerColor} font-mono`}>{stage.label}</h3>
        <span className="text-[10px] text-white/35">{skills.length}</span>
      </div>
      <SortableContext items={skills.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`grid grid-cols-2 gap-3 min-h-[80px] rounded-2xl transition-colors ${
            isOver ? overRing : ''
          } ${skills.length === 0 ? 'border border-dashed border-white/10 p-4 block' : ''}`}
        >
          {skills.length === 0 ? (
            <p className="col-span-2 text-[11px] text-white/35 text-center py-2">
              {isDone ? 'Drop a skill here when you complete it' : 'Drop a skill here'}
            </p>
          ) : (
            skills.map((s) => (
              <SkillCard key={s.id} skill={s} onOpen={onOpen} onDelete={onDelete} />
            ))
          )}
        </div>
      </SortableContext>
    </section>
  );
};

// ---------------- Growth root ----------------
export const GrowthDetail = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [active, setActive] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    try {
      const { data } = await nexaApi.get('/skills');
      setSkills(data.skills || []);
    } catch {
      toast.error('Could not load skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteSkill = async (skill) => {
    if (!window.confirm(`Delete "${skill.name}" and everything under it?`)) return;
    try {
      await nexaApi.delete(`/skills/${skill.id}`);
      setSkills((s) => s.filter((x) => x.id !== skill.id));
      toast.success('Deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  const grouped = useMemo(() => {
    const g = { in_progress: [], soon: [], future: [], done: [] };
    skills.forEach((s) => {
      const stage = STAGE_IDS.includes(s.stage) ? s.stage : DEFAULT_STAGE;
      g[stage].push(s);
    });
    return g;
  }, [skills]);

  const onDragEnd = async (event) => {
    const { active: dragged, over } = event;
    if (!dragged || !over) return;
    const draggedSkill = skills.find((s) => s.id === dragged.id);
    if (!draggedSkill) return;
    // Figure out the destination stage. `over.id` is either another skill id or a `stage:xxx` id.
    let destStage = null;
    if (typeof over.id === 'string' && over.id.startsWith('stage:')) {
      destStage = over.id.slice('stage:'.length);
    } else {
      const overSkill = skills.find((s) => s.id === over.id);
      destStage = overSkill ? (STAGE_IDS.includes(overSkill.stage) ? overSkill.stage : DEFAULT_STAGE) : null;
    }
    if (!destStage || !STAGE_IDS.includes(destStage)) return;
    const currentStage = STAGE_IDS.includes(draggedSkill.stage) ? draggedSkill.stage : DEFAULT_STAGE;
    if (destStage === currentStage) return;
    // Optimistic update
    setSkills((prev) => prev.map((s) => (s.id === dragged.id ? { ...s, stage: destStage } : s)));
    try {
      await nexaApi.patch(`/skills/${dragged.id}`, { stage: destStage });
      // Celebrate accomplishments
      if (destStage === 'done' && currentStage !== 'done') {
        try {
          const end = Date.now() + 900;
          const colors = ['#34D399', '#10B981', '#A7F3D0', '#FBBF24', '#38BDF8'];
          (function frame() {
            confetti({
              particleCount: 4,
              angle: 60,
              spread: 55,
              origin: { x: 0, y: 0.7 },
              colors,
              disableForReducedMotion: true,
            });
            confetti({
              particleCount: 4,
              angle: 120,
              spread: 55,
              origin: { x: 1, y: 0.7 },
              colors,
              disableForReducedMotion: true,
            });
            if (Date.now() < end) requestAnimationFrame(frame);
          })();
        } catch { /* confetti optional */ }
        toast.success(`Accomplished — ${draggedSkill.name}`, {
          description: 'NEXA will remember this. Proud of you.',
          duration: 4500,
        });
      }
    } catch {
      toast.error('Could not move skill');
      // Rollback
      setSkills((prev) => prev.map((s) => (s.id === dragged.id ? { ...s, stage: currentStage } : s)));
    }
  };

  if (active) {
    return (
      <SkillDetail
        skillSummary={active}
        onBack={() => { setActive(null); load(); }}
        onChanged={load}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="nexa-growth-view">
      <p className="text-white/50 text-sm leading-relaxed">
        Tell NEXA what you want to develop. She&apos;ll suggest specific books, courses, YouTube
        lessons and daily missions matched to your personality — you decide what to add.
      </p>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={() => setShowAdd(true)}
        data-testid="nexa-growth-add"
        className="w-full h-12 rounded-xl bg-white text-black font-semibold text-sm flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add a skill
      </motion.button>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-sky-400" />
        </div>
      ) : skills.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6 text-center">
          <Sparkles className="w-6 h-6 text-sky-400 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-white/60 mb-2">No skills yet.</p>
          <p className="text-[12px] text-white/40">
            Try: <span className="text-white/60">&ldquo;Talk with a deep voice&rdquo;</span>,
            <span className="text-white/60"> &ldquo;Public speaking&rdquo;</span>,
            <span className="text-white/60"> &ldquo;Learn Spanish&rdquo;</span>.
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="flex flex-col gap-6">
            {STAGES.map((stg) => (
              <StageColumn
                key={stg.id}
                stage={stg}
                skills={grouped[stg.id]}
                onOpen={setActive}
                onDelete={deleteSkill}
              />
            ))}
          </div>
        </DndContext>
      )}

      <AnimatePresence>
        {showAdd && (
          <AddSkillModal
            onClose={() => setShowAdd(false)}
            onCreated={(skill) => {
              setSkills((prev) => [{ ...skill, item_count: 0, done_count: 0 }, ...prev]);
              setActive(skill);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
